import { getZonedDate } from '@eika-infrastruktur/date';
import { useQuery, useQueryClient, QueryOptions, useMutation, QueryResult } from '@eika-infrastruktur/react-query';

import { useConfig } from '../defaultConfig';
import type { PaymentItem, PaymentItemDetails, PaymentItemId } from '../generated-types';
import {
    deletePaymentItem,
    getPaymentItemDetails,
    getPaymentItems,
    restartPaymentItem,
} from '../services/paymentService';
import { FUTURE_PAYMENTS_KEY } from './useFuturePayments';

const STOPPED_PAYMENT_KEY = ['stopped-payment-items'];
const COMBINED_STOPPED_PAYMENT_KEY = ['combined-stopped-payment-items'];

const compareDate = (a: string, b: string) => getZonedDate(a).getTime() - getZonedDate(b).getTime();
const sortByDate = (a: PaymentItem, b: PaymentItem): number => {
    return compareDate(a.dueDate, b.dueDate);
};

const byDateSelector = (efakturas: PaymentItem[]) => {
    efakturas.sort(sortByDate);
    return efakturas;
};

export const noStoppedCrossBorderPayments = (paymentItems: PaymentItem[]) =>
    paymentItems.filter((x) => x.paymentItemType !== 'FOREIGN_PAYMENT');

export const useStoppedPaymentItems = (
    enabled = true,
    options: Partial<QueryOptions<PaymentItem[]>> = {},
): QueryResult<PaymentItem[]> => {
    const { featureToggles } = useConfig();
    const queryClient = useQueryClient();
    return useQuery({
        queryKey: COMBINED_STOPPED_PAYMENT_KEY,
        queryFn: async () => {
            const stoppedPayments = await queryClient.fetchQuery<PaymentItem[]>({
                queryKey: STOPPED_PAYMENT_KEY,
                queryFn: () => getPaymentItems('STOPPED'),
            });
            if (!featureToggles.enabledStoppedPaymentsFromPending) return stoppedPayments;

            const pendingPayments = await queryClient
                .fetchQuery<PaymentItem[]>({
                    queryKey: [FUTURE_PAYMENTS_KEY],
                    queryFn: () => getPaymentItems('PENDING_PAYMENT'),
                })
                .then((payments) => payments.filter((payment) => !!payment.subStatus));
            return [...pendingPayments, ...stoppedPayments];
        },
        select: (data: PaymentItem[]) => {
            const filtered = options.select ? options.select(data) : data;
            return byDateSelector(filtered);
        },
        enabled,
    });
};

export const useRefreshPaymentItems = () => {
    const queryClient = useQueryClient();

    return () => {
        queryClient.invalidateQueries({ queryKey: [FUTURE_PAYMENTS_KEY] });
        queryClient.invalidateQueries({ queryKey: STOPPED_PAYMENT_KEY });
        queryClient.invalidateQueries({ queryKey: COMBINED_STOPPED_PAYMENT_KEY });
    };
};

export const usePaymentItemDetails = (
    id: PaymentItemId | undefined,
    options: Partial<QueryOptions<PaymentItemDetails>> = {},
): QueryResult<PaymentItemDetails> =>
    useQuery({
        queryKey: ['paymentItem', id],
        queryFn: () => getPaymentItemDetails(id!),
        ...options,
        enabled: Boolean(id) && Boolean(options.enabled),
    });

export const useRestartStoppedPayment = () =>
    useMutation({
        mutationKey: ['restart-payment-item'],
        mutationFn: restartPaymentItem,
    });
export const useDeletePaymentItem = () =>
    useMutation({
        mutationKey: ['delete-payment-item'],
        mutationFn: deletePaymentItem,
    });
